import { Injectable } from '@angular/core'
import * as Sentry from '@sentry/angular'
import { ExceptionSeverity } from './exception-severity.enum'
import { environment } from '../../../environments/environment'

export interface ExceptionContext {
  [key: string]: any
}

@Injectable({
  providedIn: 'root',
})
export class ExceptionHelperService {
  constructor() {}

  /**
   * Captures an exception with custom severity and context
   * @param error The error object
   * @param severity The severity level of the exception
   * @param context Additional context information
   * @usageNotes
   * ```typescript
   * this.exceptionHelperService.captureException(new Error('Test error'), ExceptionSeverity.ERROR, { user: 'John Doe' });
   * ```
   */
  captureException(
    error: any,
    severity: ExceptionSeverity = ExceptionSeverity.ERROR,
    context?: ExceptionContext,
  ): void {
    if (!environment.SENTRY_ENABLED) {
      console.error('Exception captured (Sentry disabled):', error)
      return
    }

    Sentry.withScope((scope) => {
      scope.setLevel(severity as Sentry.SeverityLevel)

      if (context) {
        scope.setContext('Additional Context', context)
      }

      if (error instanceof Error) {
        Sentry.captureException(error)
      } else {
        // Handle non-Error objects
        const errorMessage = typeof error === 'string' ? error : JSON.stringify(error)
        Sentry.captureMessage(errorMessage, severity as Sentry.SeverityLevel)
      }
    })
  }

  /**
   * Captures a message with custom severity and context
   * @param message The message to capture
   * @param severity The severity level
   * @param context Additional context information
   */
  captureMessage(
    message: string,
    severity: ExceptionSeverity = ExceptionSeverity.INFO,
    context?: ExceptionContext,
  ): void {
    if (!environment.SENTRY_ENABLED) {
      console.log(`Message captured (Sentry disabled): ${message}`)
      return
    }

    Sentry.withScope((scope) => {
      scope.setLevel(severity as Sentry.SeverityLevel)

      if (context) {
        scope.setContext('Additional Context', context)
      }

      Sentry.captureMessage(message, severity as Sentry.SeverityLevel)
    })
  }

  /**
   * Sets user context for better error tracking
   * @param user User information
   */
  setUserContext(user: { id?: string | number; username?: string; email?: string }): void {
    if (!environment.SENTRY_ENABLED) return

    Sentry.setUser(user)
  }

  /**
   * Clears user context
   */
  clearUserContext(): void {
    if (!environment.SENTRY_ENABLED) return

    Sentry.setUser(null)
  }

  /**
   * Adds breadcrumb for better error tracking
   * @param message Breadcrumb message
   * @param category Breadcrumb category
   * @param data Additional data
   */
  addBreadcrumb(message: string, category: string, data?: { [key: string]: any }): void {
    if (!environment.SENTRY_ENABLED) return

    Sentry.addBreadcrumb({
      message,
      category,
      data,
      level: 'info' as Sentry.SeverityLevel,
    })
  }
}
